import { useEffect } from 'react'
import ReactCountryFlag from 'react-country-flag'
import useI18n from '@/shared/hooks/usei18n'
import { trackCbamSimulationSignal } from '@/features/cbam-simulations-v2/analytics/cbamSimulationClarity'
import { Button, Card, CardBody, CardHeader, CardTitle, H2 } from '@/common'
import type { CbamSimulationGood } from '@/features/cbam-simulations-v2/hooks/cbamSimulationGoods'
import type { BatchLiabilityResultItem } from '@/features/cbam-reporting/hooks/cbamLiability'
import type { SupplierEmissionsReport } from '@/features/suppliers/hooks/supplierEmissionsReport'
import type { CbamModifiers } from '@/shared/types/cbamModifiers'
import CbamGoodEmissionsProjectionSection from '@/shared/components/cbam-good-details/CbamGoodEmissionsProjectionSection'
import CbamGoodCalculationMethodSection from '@/shared/components/cbam-good-details/CbamGoodCalculationMethodSection'
import CbamGoodDetailsDrawerLayout from '@/shared/components/cbam-good-details/CbamGoodDetailsDrawerLayout'
import useDialog from '@/common/hooks/useDialog'
import SimulationGoodLiabilityOverviewDialog from './SimulationGoodLiabilityOverviewDialog'

const formatValue = (value: string | undefined | null) => value || '-'

type SimulationGoodDetailsDrawerProps = {
  isOpen: boolean
  close: () => void
  simulationGood: CbamSimulationGood | null
  liabilityData: BatchLiabilityResultItem | null
  supplierEmissionsReport: SupplierEmissionsReport | null
  simulationYear: number
  modifiers: CbamModifiers | null
  aggregatedGoodsCategory?: string
}

export default function SimulationGoodDetailsDrawer({
  isOpen,
  close,
  simulationGood,
  liabilityData,
  supplierEmissionsReport,
  simulationYear,
  modifiers,
  aggregatedGoodsCategory
}: SimulationGoodDetailsDrawerProps) {
  const { t, dateFormatter } = useI18n()
  const {
    open: isLiabilityOverviewOpen,
    openDialog: openLiabilityOverview,
    closeDialog: closeLiabilityOverview
  } = useDialog()

  useEffect(() => {
    if (isOpen && simulationGood?.id) {
      trackCbamSimulationSignal('simulations:good:inspected')
    }
  }, [isOpen, simulationGood?.id])

  if (!simulationGood) {
    return null
  }

  return (
    <CbamGoodDetailsDrawerLayout
      isOpen={isOpen}
      close={close}
      title={
        <H2>{t('cbamSimulations:cbamSimulationDetails.simulationGoodDetailsDrawer.title')}</H2>
      }
    >
      <Card>
        <CardHeader>
          <CardTitle
            title={t(
              'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.sections.goodsAndParties'
            )}
          />
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-text-secondary mb-1">
                {t(
                  'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.cnCode'
                )}
              </div>
              <div className="font-medium">{simulationGood.cnCode}</div>
            </div>
            <div>
              <div className="text-sm text-text-secondary mb-1">
                {t(
                  'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.countryOfOrigin'
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <ReactCountryFlag countryCode={simulationGood.country} className="shrink-0" svg />
                <span>{t(`common:countriesByIsoAlpha2.${simulationGood.country}`)}</span>
              </div>
            </div>
            <div>
              <div className="text-sm text-text-secondary mb-1">
                {t(
                  'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.importDate'
                )}
              </div>
              <div>
                {simulationGood.importDate
                  ? dateFormatter(new Date(simulationGood.importDate))
                  : '-'}
              </div>
            </div>
            <div>
              <div className="text-sm text-text-secondary mb-1">
                {t(
                  'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.quarter'
                )}
              </div>
              <div>{formatValue(simulationGood.quarter ?? undefined)}</div>
            </div>
          </div>
        </CardBody>
      </Card>

      <CbamGoodEmissionsProjectionSection
        liabilityData={liabilityData}
        projectionYear={simulationYear}
        netMassInTonnes={simulationGood.netMassInTonnes}
        headerAccessory={
          liabilityData?.result && modifiers ? (
            <Button variant="subtle" onClick={openLiabilityOverview}>
              {t('common:liabilityExplanationDialog.open')}
            </Button>
          ) : undefined
        }
      />
      <CbamGoodCalculationMethodSection
        isUsingDefaultEmissions={simulationGood.isUsingDefaultEmissions}
        supplierEmissionsReport={supplierEmissionsReport}
      />
      {liabilityData?.result && modifiers ? (
        <SimulationGoodLiabilityOverviewDialog
          isOpen={isLiabilityOverviewOpen}
          close={closeLiabilityOverview}
          simulationGood={simulationGood}
          liabilityData={liabilityData}
          supplierEmissionsReport={supplierEmissionsReport}
          modifiers={modifiers}
          simulationYear={simulationYear}
          aggregatedGoodsCategory={aggregatedGoodsCategory}
        />
      ) : null}
    </CbamGoodDetailsDrawerLayout>
  )
}
